<template>
	<ValidationForm
		id="manage_cookie_banner_container"
		#default="{ handleSubmit, dirty }"
		ref="validationForm"
		class="cassie-vertical-md"
		style="width: 100%; overflow-y: auto"
		tag="div"
		@form-changed="onConfigurationFormChanged"
	>
		<CookieTemplateLayout
			v-if="showManageCookieBannerScreen"
			:header-title="'Configure Consent Banner' | useLabels('Configure Consent Banner')"
			:header-caption="'Configure a consent banner' | useLabels('Configure a consent banner')"
		>
			<template #header-after>
				<Stepper
					:steps="steps"
					:step="currentStep"
					:edit-mode="isEdit"
					:stepper-disabled="isLoading"
					@update:step="currentStep = $event"
				/>
			</template>
			<template #content>
				<!-- Skeleton Loader -->
				<div
					v-if="isLoading"
					class="skeleton-loader"
				>
					<v-card
						v-for="n in 4"
						:key="n"
						class="mb-4"
					>
						<v-skeleton-loader
							type="card-heading, divider, list-item-two-line, list-item-two-line, list-item-two-line"
							height="350"
						/>
					</v-card>
				</div>

				<!-- Main Content -->
				<StepperItems
					v-else
					:step="currentStep"
					:steps="steps"
				>
					<template #0>
						<CookieBannerConfigurationStep
							:cookie-banner.sync="cookieBanner"
							:cookie-appearance-templates="cookieAppearanceTemplates"
							:cookie-collection-templates="cookieCollectionTemplates"
							:cookie-text-templates="cookieTextTemplates"
							:is-edit="isEdit"
							:clone-mode="cloneMode"
							:cdc-cookie-collection-selected="cdcCookieCollectionSelected"
							:user-can-create-update="userCanCreateUpdate"
							:cookie-scripts="cookieScripts"
							:cookie-categories="cookieCategories"
							:headers="headers"
							:footers="footers"
							:cookie-access-keys="cookieAccessKeys"
							:use-template="useTemplate"
							@show-cookie-text-manage-screen="showManageTextTemplateScreen = true"
							@open-edit-text-template-screen="onOpenEditTextTemplateScreen"
							@show-cookie-collection-manage-screen="showManageCollectionTemplateScreen = true"
							@open-edit-collection-template-screen="onOpenEditCollectionTemplateScreen"
							@show-cookie-appearance-manage-screen="showManageAppearanceTemplateScreen = true"
							@open-edit-appearance-template-screen="onOpenEditAppearanceTemplateScreen"
							@submit="submit"
							@back-to-overview="backToOverview"
							@next="navigateToEmbedCode"
							@configuration-form-changed="onConfigurationFormChanged"
							@load-access-keys="loadAccessKeys"
						/>
					</template>
					<template #1>
						<CookieBannerInstallationStep
							:key="reloadEmbedComponent"
							:assigned-languages="languages"
							:cookie-banner-id="cookieBannerId"
							:access-key="selectedAccessKey"
							:allowed-domains="bannerDomains"
							:cdc-cookie-collection-selected="cdcCookieCollectionSelected"
							@back-to-overview="backToOverview"
							@previous="navigateToConfiguration"
						/>
					</template>
				</StepperItems>
			</template>
			<template #footer>
				<PageActionRow>
					<template #actions>
						<SecondaryActionButton @click="backToOverview">
							Back to Overview
						</SecondaryActionButton>
						<v-spacer />
						<template v-if="currentStep === 0">
							<PrimaryActionButton
								v-if="userCanCreateUpdate"
								@click="handleSubmit(submit)"
							>
								{{ isEdit ? 'Save' : 'Create' }}
							</PrimaryActionButton>
							<PrimaryActionButton
								v-if="isEdit"
								:disabled="dirty || isLoading"
								class="ml-2"
								@click="navigateToEmbedCode"
							>
								Next
							</PrimaryActionButton>
						</template>
						<template v-else>
							<SecondaryActionButton
								@click="navigateToConfiguration"
							>
								Previous
							</SecondaryActionButton>
						</template>
					</template>
				</PageActionRow>
			</template>
		</CookieTemplateLayout>
		<ManageCookieTextTemplate
			v-if="showManageTextTemplateScreen"
			:cookie-text-template-to-edit="cookieTextTemplateToEdit"
			accessed-via="manage-cookie-banner"
			@submit="submitCookieTextTemplate"
			@get-new-text-template-id="addNewTextTemplateToBanner"
			@close="closeManageCookieTextTemplateScreen"
		/>
		<ManageCookieCollectionTemplate
			v-if="showManageCollectionTemplateScreen"
			:cookie-collection-template-to-edit="cookieCollectionTemplateToEdit"
			accessed-via="manage-cookie-banner"
			@submit="submitCookieCollectionTemplate"
			@get-new-collection-template-id="addNewCollectionTemplateToBanner"
			@close="closeManageCookieCollectionTemplateScreen"
		/>
		<ManageCookieAppearanceTemplate
			v-if="showManageAppearanceTemplateScreen"
			:cookie-appearance-template-to-edit="cookieAppearanceTemplateToEdit"
			accessed-via="manage-cookie-banner"
			@submit="submitCookieAppearanceTemplate"
			@get-new-appearance-template-id="addNewAppearanceTemplateToBanner"
			@close="closeManageCookieAppearanceTemplateScreen"
		/>
	</ValidationForm>
</template>

<script>
import { mapGetters } from 'vuex'
import ValidationForm from '../../../../../../../shared/components/validation-form.vue'
import CookieTemplateLayout from '../cookie-template-layout.vue'
import Stepper from '../../../../../../../shared/components/stepper.vue'
import StepperItems from '../../../../../../../shared/components/stepper-items.vue'
import CookieBannerConfigurationStep from './cookie-banner-configuration-step.vue'
import CookieBannerInstallationStep from './cookie-banner-installation-step.vue'
import ManageCookieTextTemplate from '../cookie-text-templates/_manage-cookie-text-template.vue'
import ManageCookieAppearanceTemplate from '../cookie-appearances-templates/_manage-cookie-appearance-template.vue'
import ManageCookieCollectionTemplate from '../cookie-collection-templates/_manage-cookie-collection-template.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import PageActionRow from '../../../../../../../shared/components/page-action-row.vue'
import { COOKIE_BANNERS } from '../../../../../router/route-names.js'
import { getAssignedLanguages } from '../../../../../../../shared/utils/api/languages.js'
import { postCookieBanner, putCookieBanner } from '../../../../../../../shared/utils/api/cookies/cookie-banners.js'
import { getCookieAppearanceTemplates } from '../../../../../../../shared/utils/api/cookies/cookie-appearance-templates.js'
import { getCookieTextTemplates } from '../../../../../../../shared/utils/api/cookies/cookie-text-templates.js'
import { getCookieCollectionTemplates } from '../../../../../../../shared/utils/api/cookies/cookie-collection-templates.js'
import { getCookieScripts } from '../../../../../../../shared/utils/api/cookies/cookie-scripts.js'
import { getCookieCategories } from '../../../../../../../shared/utils/api/cookies/cookie-categories.js'
import { getFooters, getHeaders } from '../../../../../../../shared/utils/api/headers-and-footers.js'
import { headersAndFootersTypeIdEnum } from '../../../../../../../shared/enums/headers-and-footers.js'
import { CAN_CREATE_UPDATE_COOKIE_BANNERS } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { getConsentBannerAccessKeys } from '../../../../../../../shared/utils/api/access-keys.js'
import { defaultBrandInSelect, userHasBrand } from '../../../../../../../shared/state/brands.js'
import {
	COLOUR_SCHEME_IDENTIFIERS,
	PRE_BANNER_LIGHT_COLOUR_SCHEME,
	GPC_BANNER_LIGHT_COLOUR_SCHEME,
	BANNER_LIGHT_COLOUR_SCHEME
} from '../cookie-appearances-templates//appearance-enums.js'
import { setFormChangedValue } from '../../../../../../../shared/state/validation-observers.js'
import { templateIds, TEMPLATE_NAMES } from './banner-templates.js'
export default {
	components: {
		ValidationForm,
		CookieTemplateLayout,
		Stepper,
		StepperItems,
		CookieBannerConfigurationStep,
		CookieBannerInstallationStep,
		ManageCookieTextTemplate,
		ManageCookieCollectionTemplate,
		ManageCookieAppearanceTemplate,
		PrimaryActionButton,
		SecondaryActionButton,
		PageActionRow
	},
	props: {
		cookieBannerToEdit: Object,
		cloneMode: {
			type: Boolean,
			default: false
		},
		useTemplate: {
			type: Boolean,
			default: false
		},
		templateType: {
			type: String,
			default: null
		}
	},
	setup () {
		return { defaultBrandInSelect, setFormChangedValue }
	},
	data () {
		return {
			currentStep: this.initialStep || 0,
			steps: [
				{ slot: 0, title: 'Configuration', stepNumber: 0, stepComplete: false },
				{ slot: 1, title: 'Embed Code', stepNumber: 1, stepComplete: false }
			],
			cookieBanner: JSON.parse(JSON.stringify(this.cookieBannerToEdit || {
				name: '',
				description: '',
				brandId: this.defaultBrandInSelect,
				enableCis: false, // remove value before calling API
				identityServiceEndpoint: null,
				cisCookies: [],
				privacyPolicyId: null,
				cookieTextTemplateId: null,
				cookieAppearanceTemplateId: null,
				cookieCollectionTemplateId: null,
				accessKeyId: null,
				allowedDomainsValue: true, // remove value before calling API
				allowedDomains: [],
				cassieCookiesExpiryInDays: 365,
				rejectAllCookiesExpiryInDays: null,
				metaData: [],
				mspaSharingNotice: null,
				mspaSaleOptOutNotice: null,
				mspaSharingOptOutNotice: null,
				mspaTargetedAdvertisingOptOutNotice: null,
				mspaCoveredTransaction: 1,
				mspaMode: 1
			})),
			languages: [],
			cookieCollectionTemplates: [],
			cookieTextTemplates: [],
			cookieAppearanceTemplates: [],
			cookieScripts: [],
			cookieCategories: [],
			headers: [],
			footers: [],
			showManageCollectionTemplateScreen: false,
			cookieCollectionTemplateToEdit: null,
			showManageTextTemplateScreen: false,
			cookieTextTemplateToEdit: null,
			showManageAppearanceTemplateScreen: false,
			cookieAppearanceTemplateToEdit: null,
			cookieBannerId: null,
			cookieAccessKeys: [],
			configurationFormChanged: false,
			reloadEmbedComponent: false,
			isLoading: true
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdate () {
			if (!this.isEdit) {
				return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_BANNERS)
			} else {
				return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_BANNERS) && userHasBrand(this.cookieBanner.brandId)
			}
		},
		isEdit () {
			return (!!this.cookieBannerToEdit || !!this.cookieBannerId) && !this.cloneMode
		},
		initialStep () {
			return this.isEdit ? 1 : 0
		},
		showManageCookieBannerScreen () {
			return !this.showManageCollectionTemplateScreen && !this.showManageTextTemplateScreen && !this.showManageAppearanceTemplateScreen
		},
		selectedAccessKey () {
			const selectedAccessKey = this.cookieAccessKeys.find(accessKey => accessKey.accessKeyId === this.cookieBanner.accessKeyId)?.accessKey
			return selectedAccessKey || ''
		},
		cdcCookieCollectionSelected () {
			return this.cookieCollectionTemplates?.find(({ id }) => id === this.cookieBanner.cookieCollectionTemplateId)?.crossDomainConsent
		},
		bannerDomains () {
			if (Array.isArray(this.cookieBanner.allowedDomains)) {
				return this.cookieBanner.allowedDomains?.map(domain => ({
					text: domain,
					value: domain
				}))
			} else {
				return this.cookieBanner.allowedDomains?.split(',').map(domain => ({
					text: domain,
					value: domain
				}))
			}
		}
	},
	async created () {
		this.ensureMspaDefaults()
		await Promise.all([
			this.getCookieCollectionTemplates(),
			this.getCookieTextTemplates(),
			this.getCookieAppearanceTemplates(),
			this.getLanguages(),
			this.getCookieScripts(),
			this.getCookieCategories(),
			this.loadHeaders(),
			this.loadFooters(),
			this.loadAccessKeys()
		])
		if (this.useTemplate) {
			await this.setTemplates()
		}
		if (this.isEdit) {
			this.cookieBannerId = this.cookieBannerToEdit.cookieBannerId
			this.setAllStepsToComplete()
		}
		if (this.cloneMode) {
			this.$refs.validationForm.$refs.validationObserver.flags.dirty = true
		}
		this.isLoading = false
	},
	methods: {
		ensureMspaDefaults () {
			// Ensure MSPA properties have default values if they don't exist
			if (this.cookieBanner.mspaCoveredTransaction === undefined || this.cookieBanner.mspaCoveredTransaction === null) {
				this.$set(this.cookieBanner, 'mspaCoveredTransaction', 1)
			}
			if (this.cookieBanner.mspaMode === undefined || this.cookieBanner.mspaMode === null) {
				this.$set(this.cookieBanner, 'mspaMode', 1)
			}
		},
		async getCookieCollectionTemplates () {
			const { cookieCollectionTemplates } = await getCookieCollectionTemplates()
			this.cookieCollectionTemplates = cookieCollectionTemplates
		},
		async getCookieTextTemplates () {
			const { cookieTextTemplates } = await getCookieTextTemplates()
			this.cookieTextTemplates = cookieTextTemplates
		},
		async getCookieAppearanceTemplates () {
			const { cookieAppearanceTemplates } = await getCookieAppearanceTemplates()
			this.cookieAppearanceTemplates = cookieAppearanceTemplates
			if (this.cookieAppearanceTemplates && this.cookieAppearanceTemplates.length > 0) {
				this.cookieAppearanceTemplates.forEach(cookieAppearanceTemplate => {
					if (!cookieAppearanceTemplate.preBanner.preBannerColourSchemeJSON) {
						cookieAppearanceTemplate.preBanner.preBannerColourSchemeIdentifier = COLOUR_SCHEME_IDENTIFIERS.NONE
						cookieAppearanceTemplate.preBanner.preBannerColourSchemeJSON = PRE_BANNER_LIGHT_COLOUR_SCHEME
					}
					if (!cookieAppearanceTemplate.gpcBanner.gpcBannerColourSchemeJSON) {
						cookieAppearanceTemplate.gpcBanner.gpcBannerColourSchemeIdentifier = COLOUR_SCHEME_IDENTIFIERS.NONE
						cookieAppearanceTemplate.gpcBanner.gpcBannerColourSchemeJSON = GPC_BANNER_LIGHT_COLOUR_SCHEME
					}
					if (!cookieAppearanceTemplate.banner.bannerColourSchemeJSON) {
						cookieAppearanceTemplate.banner.bannerColourSchemeIdentifier = COLOUR_SCHEME_IDENTIFIERS.NONE
						cookieAppearanceTemplate.banner.bannerColourSchemeJSON = BANNER_LIGHT_COLOUR_SCHEME
					}
				})
			}
		},
		async getCookieScripts () {
			const { cookieScripts } = await getCookieScripts()
			this.cookieScripts = cookieScripts
		},
		async getCookieCategories () {
			const { cookieCategories } = await getCookieCategories()
			this.cookieCategories = cookieCategories
		},
		async loadHeaders () {
			const { data: headers } = await getHeaders(headersAndFootersTypeIdEnum.CONSENT_BANNERS)
			this.headers = headers ?? []
		},
		async loadFooters () {
			const { data: footers } = await getFooters(headersAndFootersTypeIdEnum.CONSENT_BANNERS)
			this.footers = footers ?? []
		},
		async loadAccessKeys () {
			const { cookieAccessKeys } = await getConsentBannerAccessKeys()
			this.cookieAccessKeys = cookieAccessKeys
		},
		backToOverview () {
			this.$router.push({ name: COOKIE_BANNERS })
		},
		setAllStepsToComplete () {
			this.steps.forEach(step => {
				step.stepComplete = true
			})
		},
		navigateForward () {
			this.steps[this.currentStep].stepComplete = true
			this.currentStep++
		},
		navigateToEmbedCode () {
			this.currentStep = 1
		},
		// text template events
		closeManageCookieTextTemplateScreen () {
			this.showManageTextTemplateScreen = false
			this.cookieTextTemplateToEdit = null
		},
		submitCookieTextTemplate () {
			this.closeManageCookieTextTemplateScreen()
			this.getCookieTextTemplates()
		},
		onOpenEditTextTemplateScreen (selectedTextTemplateId) {
			this.showManageTextTemplateScreen = true
			this.cookieTextTemplateToEdit = this.cookieTextTemplates.find(template => template.id === selectedTextTemplateId)
		},
		addNewTextTemplateToBanner (newTextTemplateId) {
			this.cookieBanner.cookieTextTemplateId = newTextTemplateId
		},
		// collection template events
		closeManageCookieCollectionTemplateScreen () {
			this.showManageCollectionTemplateScreen = false
			this.cookieCollectionTemplateToEdit = null
		},
		submitCookieCollectionTemplate () {
			this.closeManageCookieCollectionTemplateScreen()
			this.getCookieCollectionTemplates()
		},
		onOpenEditCollectionTemplateScreen (selectedCollectionTemplateId) {
			this.showManageCollectionTemplateScreen = true
			this.cookieCollectionTemplateToEdit = this.cookieCollectionTemplates.find(template => template.id === selectedCollectionTemplateId)
		},
		addNewCollectionTemplateToBanner (newCollectionTemplateId) {
			this.cookieBanner.cookieCollectionTemplateId = newCollectionTemplateId
		},
		// appearance template events
		closeManageCookieAppearanceTemplateScreen () {
			this.showManageAppearanceTemplateScreen = false
			this.cookieAppearanceTemplateToEdit = null
		},
		submitCookieAppearanceTemplate () {
			this.closeManageCookieAppearanceTemplateScreen()
			this.getCookieAppearanceTemplates()
		},
		onOpenEditAppearanceTemplateScreen (selectedAppearanceTemplateId) {
			this.showManageAppearanceTemplateScreen = true
			this.cookieAppearanceTemplateToEdit = this.cookieAppearanceTemplates.find(template => template.id === selectedAppearanceTemplateId)
		},
		addNewAppearanceTemplateToBanner (newAppearanceTemplateId) {
			this.cookieBanner.cookieAppearanceTemplateId = newAppearanceTemplateId
		},
		async submit () {
			let banner = JSON.parse(JSON.stringify(this.cookieBanner))
			banner.identityServiceEnabled = banner.enableCis
			banner.identityServiceEndpoint = !this.cdcCookieCollectionSelected ? banner.identityServiceEndpoint : null
			if (!banner.allowedDomainsValue) {
				banner.allowedDomains = banner.allowedDomains.join(',')
			} else {
				banner.allowedDomains = ''
			}
			if (!banner.identityServiceEnabled) {
				banner.identityServiceEndpoint = null
				banner.cisCookies = []
			}
			if (banner.metaData) {
				banner.metaData = banner.metaData.map(meta => ({
					externalMetaDataId: meta.metaDataKeyId,
					externalDataValue: meta.metaDataValue
				}))
			}
			delete banner.allowedDomainsValue
			banner.description = banner.name
			if (!this.isEdit) {
				await postCookieBanner(banner).then(response => {
					this.cookieBannerId = response.data.newCookieBannerId
				})
			} else {
				if (!banner.cookieBannerId) banner = { ...banner, cookieBannerId: this.cookieBannerId }
				await putCookieBanner(banner)
			}
			this.reloadEmbedComponent = !this.reloadEmbedComponent
			setFormChangedValue(false)
			await this.$nextTick()
			this.navigateToEmbedCode()
		},
		async getLanguages () {
			const { data: assignedLanguages } = await getAssignedLanguages()
			this.languages = assignedLanguages
		},
		navigateToConfiguration () {
			this.currentStep = 0
		},
		onConfigurationFormChanged (changed) {
			this.configurationFormChanged = changed
		},
		setTemplates () {
			const defaultTextTemplates = this.cookieTextTemplates.filter(template => template.isSystem)
			const defaultAppearanceTemplates = this.cookieAppearanceTemplates.filter(template => template.isSystem)

			switch (this.templateType) {
				case templateIds.GDPR: {
					const gdprTextTemplate = defaultTextTemplates.find(template => template.name === TEMPLATE_NAMES.GDPR_TEXT)
					if (gdprTextTemplate) {
						this.cookieBanner.cookieTextTemplateId = gdprTextTemplate.id
					}
					const gdprAppearanceTemplate = defaultAppearanceTemplates.find(template => template.name === TEMPLATE_NAMES.GDPR_APPEARANCE)
					if (gdprAppearanceTemplate) {
						this.cookieBanner.cookieAppearanceTemplateId = gdprAppearanceTemplate.id
					}
					break
				}
				case templateIds.CCPA: {
					const ccpaTextTemplate = defaultTextTemplates.find(template => template.name === TEMPLATE_NAMES.CCPA_TEXT)
					if (ccpaTextTemplate) {
						this.cookieBanner.cookieTextTemplateId = ccpaTextTemplate.id
					}
					const ccpaAppearanceTemplate = defaultAppearanceTemplates.find(template => template.name === TEMPLATE_NAMES.CCPA_APPEARANCE)
					if (ccpaAppearanceTemplate) {
						this.cookieBanner.cookieAppearanceTemplateId = ccpaAppearanceTemplate.id
					}
					break
				}
				case templateIds.IAB_EU: {
					const iabEuTextTemplate = defaultTextTemplates.find(template => template.name === TEMPLATE_NAMES.IAB_EU_TEXT)
					if (iabEuTextTemplate) {
						this.cookieBanner.cookieTextTemplateId = iabEuTextTemplate.id
					}
					const iabEuAppearanceTemplate = defaultAppearanceTemplates.find(template => template.name === TEMPLATE_NAMES.IAB_EU_APPEARANCE)
					if (iabEuAppearanceTemplate) {
						this.cookieBanner.cookieAppearanceTemplateId = iabEuAppearanceTemplate.id
					}
					break
				}
				case templateIds.MSPA: {
					const mspaTextTemplate = defaultTextTemplates.find(template => template.name === TEMPLATE_NAMES.MSPA_TEXT)
					if (mspaTextTemplate) {
						this.cookieBanner.cookieTextTemplateId = mspaTextTemplate.id
					}
					const mspaAppearanceTemplate = defaultAppearanceTemplates.find(template => template.name === TEMPLATE_NAMES.MSPA_APPEARANCE)
					if (mspaAppearanceTemplate) {
						this.cookieBanner.cookieAppearanceTemplateId = mspaAppearanceTemplate.id
					}
					break
				}
				case templateIds.NOTICE: {
					const noticeTextTemplate = defaultTextTemplates.find(template => template.name === TEMPLATE_NAMES.NOTICE_TEXT)
					if (noticeTextTemplate) {
						this.cookieBanner.cookieTextTemplateId = noticeTextTemplate.id
					}
					const noticeAppearanceTemplate = defaultAppearanceTemplates.find(template => template.name === TEMPLATE_NAMES.NOTICE_APPEARANCE)
					if (noticeAppearanceTemplate) {
						this.cookieBanner.cookieAppearanceTemplateId = noticeAppearanceTemplate.id
					}
					break
				}
			}
		}
	}
}
</script>
